/**
 * Terms and Conditions Page
 * Legal terms for using Kata.fit
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const TermsAndConditionsPage = () => {
  const effectiveDate = 'February 1, 2026';
  const lastUpdated = 'February 1, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="back-link">← Back to App</Link>
          <h1>Terms and Conditions</h1>
          <p className="meta">Effective Date: {effectiveDate} | Last Updated: {lastUpdated}</p>
        </header>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Kata.fit (the "Service"), you agree to be bound by these Terms 
            and Conditions ("Terms"). If you do not agree to these Terms, please do not use the Service.
          </p>
          <p>
            Kata.fit is a fitness tracking and AI-powered application that helps users track workouts, 
            nutrition, and receive personalized training guidance.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>Kata.fit provides:</p>
          <ul>
            <li><strong>Free Tier:</strong> Workout tracking, meal logging, body metrics, progress photos, 
            social features including joining training dojos, and basic statistics.</li>
            <li><strong>Pro Tier:</strong> AI-powered training recommendations, real-time exercise guidance, 
            pose detection and form feedback, nutrition planning, and the ability to create and manage dojos.</li>
          </ul>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time 
            with reasonable notice when possible.
          </p>
        </section>

        <section>
          <h2>3. User Accounts</h2>
          <p>
            To use Kata.fit, you must create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate and complete information during registration</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </ul>
          <p>
            You must be at least 16 years old to use the Service. If you are under 18, you represent 
            that you have parental consent.
          </p>
        </section>

        <section>
          <h2>4. Subscription and Billing</h2>
          <p>
            Pro subscriptions are processed through Paddle.com ("Paddle"), our payment processor. 
            By subscribing to Pro:
          </p>
          <ul>
            <li>You authorize Paddle to charge your payment method on a recurring basis</li>
            <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
            <li>Prices may change with 30 days notice</li>
            <li>Refunds are subject to our <Link to="/refund">Refund Policy</Link></li>
          </ul>
          <p>
            Paddle acts as the Merchant of Record and handles all payment processing, invoicing, 
            and sales tax. Paddle's terms apply to payment transactions.
          </p>
        </section>

        <section>
          <h2>5. Technology Partners and Third-Party Services</h2>
          <p>
            The Service relies on several third-party providers to function. By using the Service, 
            you acknowledge that:
          </p>
          <ul>
            <li><strong>AI Features:</strong> Powered by OpenRouter. AI outputs are probabilistic and may be inaccurate.</li>
            <li><strong>Authentication:</strong> Identity services are managed by Clerk.com.</li>
            <li><strong>Hosting & Database:</strong> Infrastructure is provided by Heroku (Salesforce) and MongoDB Atlas.</li>
            <li><strong>Communications:</strong> Email delivery is handled by Resend.</li>
          </ul>
          <p>
            We are not liable for service interruptions, data breaches, or failures caused specifically 
            by these third-party providers, provided we have exercised reasonable diligence in selecting them.
          </p>
          <p>
            For more information about how OpenRouter processes data, please review their terms at{' '}
            <a href="https://openrouter.ai/terms" target="_blank" rel="noopener noreferrer">
              openrouter.ai/terms
            </a>.
          </p>
        </section>

        <section>
          <h2>6. Privacy and Data Usage</h2>
          <p>
            Your use of the Service is also governed by our <Link to="/privacy">Privacy Policy</Link>. 
            We specifically note the following sensitive features:
          </p>
          <ul>
            <li><strong>Geolocation:</strong> Location tracking is disabled by default. If you enable it 
            in your settings, we may access your location data to detect when you are at a gym 
            and automatically associate workouts with that location. You can disable this at any time 
            in your profile settings.</li>
            <li><strong>Contact List:</strong> If you use the friend invite feature, we only process the 
            specific contacts you select to send the invitation. We do not store your full contact list.</li>
          </ul>
        </section>

        <section>
          <h2>7. User Content</h2>
          <p>
            You retain ownership of content you submit (workouts, photos, etc.). By submitting content, 
            you grant Kata.fit a non-exclusive, worldwide license to use, store, and process your 
            content solely for providing the Service.
          </p>
          <p>You agree not to submit content that:</p>
          <ul>
            <li>Violates any law or third-party rights</li>
            <li>Contains malware or harmful code</li>
            <li>Is fraudulent, misleading, or deceptive</li>
            <li>Harasses, threatens, or intimidates others</li>
            <li>Contains inappropriate or explicit material</li>
          </ul>
          <p>
            We have zero tolerance for objectionable content or abusive behavior. 
            We reserve the right to ban any user who engages in harassment, hate speech, 
            or posts explicit content, without refund. We reserve the right to remove 
            content that violates these Terms.
          </p>
        </section>

        <section>
          <h2>8. Dojos and Social Features</h2>
          <p>
            Dojos are training groups where users can share progress and train together. 
            Dojo creators ("Sensei") are responsible for:
          </p>
          <ul>
            <li>Setting appropriate requirements and rules for their dojo</li>
            <li>Moderating member behavior within their dojo</li>
            <li>Respecting member privacy and data</li>
          </ul>
          <p>
            Members who share data with a dojo consent to that data being visible to other 
            dojo members according to their privacy settings.
          </p>
        </section>

        <section>
          <h2>9. Prohibited Uses</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any illegal purpose</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with or disrupt the Service</li>
            <li>Scrape, crawl, or extract data without permission</li>
            <li>Resell or redistribute the Service</li>
            <li>Impersonate others or misrepresent your identity</li>
            <li>Use automated tools to access the Service excessively</li>
          </ul>
        </section>

        <section>
          <h2>10. Health and Safety Disclaimer</h2>
          <p className="warning">
            <strong>⚠️ Important:</strong> Kata.fit is not a substitute for professional medical advice, 
            diagnosis, or treatment. Always consult with qualified healthcare providers before beginning 
            any exercise program, especially if you have health conditions or injuries.
          </p>
          <ul>
            <li>Listen to your body and stop exercising if you feel pain or discomfort</li>
            <li>AI recommendations are suggestions only and may not be suitable for everyone</li>
            <li>We are not responsible for injuries resulting from following workout suggestions</li>
            <li>Pose detection is for feedback purposes and should not replace proper instruction</li>
          </ul>
        </section>

        <section>
          <h2>11. Intellectual Property</h2>
          <p>
            The Service, including its design, code, features, and content (excluding user content), 
            is owned by Kata.fit and protected by intellectual property laws. You may not copy, modify, 
            or distribute any part of the Service without permission.
          </p>
        </section>

        <section>
          <h2>12. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Kata.fit and its operators shall not be liable for:
          </p>
          <ul>
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of data, profits, or business opportunities</li>
            <li>Injuries or health issues arising from use of the Service</li>
            <li>Service interruptions or downtime</li>
            <li>Actions of third-party services (Paddle, OpenRouter, etc.)</li>
          </ul>
          <p>
            Our total liability shall not exceed the amount you paid for the Service in the 
            12 months preceding the claim.
          </p>
        </section>

        <section>
          <h2>13. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Kata.fit from any claims, damages, or expenses 
            arising from your use of the Service, violation of these Terms, or infringement of 
            third-party rights.
          </p>
        </section>

        <section>
          <h2>14. Termination</h2>
          <p>
            We may suspend or terminate your account for violations of these Terms or for any other 
            reason with reasonable notice. Upon termination:
          </p>
          <ul>
            <li>Your right to use the Service ends immediately</li>
            <li>You may request export of your data within 30 days</li>
            <li>We may delete your data after a reasonable retention period</li>
          </ul>
          <p>
            You may delete your account at any time through the app settings.
          </p>
        </section>

        <section>
          <h2>15. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of significant changes 
            via email or in-app notification. Continued use after changes constitutes acceptance 
            of the updated Terms.
          </p>
        </section>

        <section>
          <h2>16. Governing Law</h2>
          <p>
            These Terms are governed by the laws of Delaware, United States, 
            without regard to conflict of law principles. Any disputes shall be resolved exclusively in the 
            courts of Delaware, United States.
          </p>
        </section>

        <section>
          <h2>17. Contact</h2>
          <p>
            For questions about these Terms, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> team@kata.fit<br />
            <strong>Website:</strong> kata.fit
          </p>
        </section>

        <footer className="legal-footer">
          <div className="footer-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/refund">Refund Policy</Link>
            <Link to="/support">Support</Link>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Kata.fit. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
